import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

type RemoveType = 'today' | 'future';

interface Props {
  memberName: string;
  onConfirm: (type: RemoveType) => void;
  onClose: () => void;
  isPending?: boolean;
}

export default function ScrumMemberRemoveModal({ memberName, onConfirm, onClose, isPending }: Props) {
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [selectedType, setSelectedType] = useState<RemoveType | null>(null);

  const handleSelect = (type: RemoveType) => {
    setSelectedType(type);
    setStep('confirm');
  };

  const handleConfirm = () => {
    if (selectedType) onConfirm(selectedType);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[400px] max-w-[95vw] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">멤버 제거</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="size-4" /></button>
        </div>

        {step === 'select' ? (
          <>
            <p className="text-sm text-gray-600">
              <strong>{memberName}</strong>을(를) 어떻게 제거하시겠습니까?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => handleSelect('today')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
              >
                <p className="text-sm font-medium text-gray-900">오늘 스크럼에서만 제거</p>
                <p className="text-sm text-gray-500 mt-0.5">이전/이후 스크럼에는 영향 없음</p>
              </button>
              <button
                onClick={() => handleSelect('future')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50/50 transition-colors"
              >
                <p className="text-sm font-medium text-gray-900">스크럼 멤버에서 제거</p>
                <p className="text-sm text-gray-500 mt-0.5">오늘 포함 이후 모든 스크럼에서 제거 (이전 기록은 유지)</p>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {selectedType === 'today'
                    ? `${memberName}을(를) 오늘 스크럼에서 제거합니다.`
                    : `${memberName}을(를) 스크럼 멤버에서 제거합니다.`}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {selectedType === 'today'
                    ? '입력된 내용이 삭제됩니다. 이 작업은 되돌릴 수 없습니다.'
                    : '오늘 포함 이후 모든 스크럼에서 해당 멤버가 제거됩니다. 이전 기록은 유지됩니다.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStep('select')}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                이전
              </button>
              <button
                onClick={handleConfirm}
                disabled={isPending}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isPending ? '처리 중...' : '제거'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
